import { useQuery } from '@apollo/react-hooks';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import { StackView, Text } from 'localmed-core';
import uniqBy from 'lodash/uniqBy';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RENDER_PHASE, UniversalContext } from '../../../_universal';
import { getTimeZoneName, isCurrentTimeZone } from '../../../legacyCore/OpeningsCalendar/dateUtils';
import { Alert, RadioGroup } from '../../../shared/components/DiComponents';
import DiErrorModal from '../../../shared/components/DiComponents/DiModal/DiErrorModal';
import RadioGroupItemWrapper from '../../../shared/components/DiComponents/RadioGroup/RadioGroupItemWrapper';
import { DiGenericLoading } from '../../../shared/components/DiLayout';
import {
  addCurrentAppointmentAsOpening,
  removeUnavailableOpenings,
} from '../../../shared/components/RescheduleForm/utils';
import { DATE_FORMAT, LM_CLIENT_CONTEXT } from '../../../shared/constants';
import { formatDate, formatOpeningTime } from '../../../shared/utils/formatCalendarTime';
import getTimeZoneFormat from '../../../shared/utils/getTimeZoneFormat';
import supportsTimeZoneFormat from '../../../shared/utils/supportsTimeZoneFormat';
import type { IOpening, ThemeProps } from '../../../types';
import { ISchedulingValues } from '../../pages/SchedulingPage/components/schedulingWizard';
import query from './queries/SchedulingOpeningsQuery.graphql';

const TimeSelectionWrapper = styled(StackView)(
  ({ theme }: ThemeProps) => css`
    max-width: ${theme.breakpoints.md};
  `
);

interface IProps {
  values: ISchedulingValues;
  setValue: (name: string, value: any) => void;
  hasOpeningUnavailableError?: boolean;
  currentDate: Date;
  nextOpening?: IOpening;
  datePickerLoading: boolean;
  unavailableOpenings?: Array<IOpening>;
}

function isEmpty(values?: any[]): boolean {
  return !values || values.length === 0;
}

export default function SchedulingOpeningSection({
  values,
  hasOpeningUnavailableError,
  currentDate,
  datePickerLoading,
  setValue,
  unavailableOpenings = [],
}: IProps) {
  const intl = useIntl();
  const [modalOpen, setModalOpen] = useState(false);

  const openingsRef = useRef<any>([]);
  const universalContext = React.useContext(UniversalContext);

  const isClientRender =
    universalContext == null || universalContext.renderPhase === RENDER_PHASE.CLIENT_RENDER;
  const { opening, provider, queryProvider, reasonForVisit, office, appointment } = values;

  const RadioButton = ({ time, selected }) => (
    <RadioGroupItemWrapper selected={selected} width={70} fontSize="xs" padding="xs">
      {formatOpeningTime(time, intl, getTimeZoneFormat(office?.timeZoneCode))}
    </RadioGroupItemWrapper>
  );

  const openingQueryVars = queryProvider?.id
    ? { providerId: queryProvider?.id }
    : { officeId: office?.id };

  const { data: openingsData, loading, error } = useQuery(query, {
    variables: {
      ...openingQueryVars,
      reasonForVisitId: reasonForVisit?.id,
      before: endOfDay(currentDate).toISOString(),
      after: startOfDay(currentDate).toISOString(),
    },
    context: LM_CLIENT_CONTEXT,
  });

  if (openingsData?.openings) {
    if (appointment) {
      openingsRef.current = addCurrentAppointmentAsOpening(
        removeUnavailableOpenings(openingsData?.openings.nodes || [], unavailableOpenings),
        appointment
      );
    } else {
      openingsRef.current = removeUnavailableOpenings(
        openingsData?.openings.nodes || [],
        unavailableOpenings
      );
    }
  }

  useEffect(() => {
    if (error) {
      setModalOpen(true);
    }
  }, [error]);

  if (modalOpen) {
    return (
      <DiErrorModal
        open
        closeModal={() => {
          setModalOpen(false);
        }}
        text="We could not load the available times."
      />
    );
  }

  const noOpenings =
    !loading && isEmpty(openingsRef.current) && !openingsData?.pageInfo?.nextAvailableDate;

  let content: ReactNode;

  if (loading) {
    content = !datePickerLoading ? <DiGenericLoading /> : null;
  } else if (isEmpty(openingsRef.current) || noOpenings) {
    content = (
      <StackView spacing="md">
        <Text>
          <FormattedMessage
            id="scheduling.SchedulingTimeSelectionStep.noAvailableMessage"
            defaultMessage="{providerSelected, select, true {{providerDisplay} does not} false {We do not}} have any online openings for {reasonForVisitDisplay} this day."
            values={{
              providerSelected: provider != null,
              providerDisplay: provider?.display,
              reasonForVisitDisplay: reasonForVisit?.name || 'your reason for visit',
            }}
          />
        </Text>
      </StackView>
    );
  } else {
    const uniqOpenings = uniqBy(openingsRef.current, 'time');
    content = (
      <RadioGroup
        maxWidth={{ _: '260px', lg: '100%' }}
        justifyContent="center"
        name="times"
        items={uniqOpenings}
        itemDisplayComponent={RadioButton}
        onChange={(newOpening: IOpening) => {
          setValue('opening', newOpening);
          setValue('openingError', false);
          if (newOpening.provider.id !== provider?.id) {
            setValue('provider', newOpening.provider);
          }
        }}
        value={opening}
      />
    );
  }

  let AlertContent: ReactNode;

  if (isClientRender && !isCurrentTimeZone(office?.timeZoneCode)) {
    if (supportsTimeZoneFormat()) {
      const firstTime = openingsRef.current.length && openingsRef.current[0].time;
      const timeZoneName =
        getTimeZoneName(intl, firstTime, office?.timeZoneCode) || office?.timeZoneCode;
      AlertContent = (
        <Alert type="warning" p="md" my="md" hideTitle inline>
          <FormattedMessage
            id="scheduling.SchedulingTimeSelectionStep.localTimeZoneMessage"
            defaultMessage="These open appointments are in a different time zone: {timeZoneName}"
            values={{
              timeZoneName,
            }}
          />
        </Alert>
      );
    } else {
      AlertContent = (
        <Alert type="warning" p="md" my="md" inline>
          <FormattedMessage
            id="scheduling.SchedulingTimeSelectionStep.localTimeZoneMessage"
            defaultMessage="These open appointments are shown in your local time zone, which may be different from the
          office’s."
          />
        </Alert>
      );
    }
  }

  return (
    <TimeSelectionWrapper spacing="md" ml={{ _: 'none', lg: 'xl' }} width="100%">
      {hasOpeningUnavailableError && (
        <Alert type="error" p="md" m="md">
          <FormattedMessage
            id="scheduling.SchedulingTimeSelectionStep.openingErrorMessage"
            defaultMessage="This time is no longer available."
          />
        </Alert>
      )}
      {AlertContent}
      <Text fontWeight="700" fontSize="sm" textAlign="center">
        <FormattedMessage
          id="scheduling.SchedulingTimeSelectionStep.availableTimesMessage"
          defaultMessage="Available Times"
        />{' '}
        {currentDate
          ? `for ${formatDate(intl, currentDate, null, {
              intl: DATE_FORMAT.LONG,
              fallback: DATE_FORMAT.LONG_FALLBACK,
            })}`
          : ':'}
      </Text>
      {content}
    </TimeSelectionWrapper>
  );
}
